/* eslint-disable react/no-string-refs */
import { useFlags, withLDConsumer } from "launchdarkly-react-client-sdk";
import queryString from "query-string";
import React from "react";
import NotificationSystem from "react-notification-system";
import { withRouter } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import { ThemeProvider } from "styled-components";
import { notificationStyle } from "./EndUserLayout.styled";

import {
  ModalGeneric,
  NavBar,
  Store__AtsStats,
  Store__System
} from "@talentbait/shared-components";
import "@talentbait/shared-components/public/build/app.css";

import Actions from "../../Actions";
import CampaignMembersStore from "../../Components/CampaignMembers/CampaignMembers.store";
import theme from "../../theme";
import { PAGES_WITHOUT_NAVBAR } from "../../constants";

import logger from "../../utils/LoggerFrontend";
import StatusStore from "../../Stores/StatusStore";
import CreditModal from "../../PagesUser/credit-modal/CreditModal";
import { getPermissions } from "../../Permissions";
import { useTranslation } from "react-i18next";
import CreateInvoiceAddressModal from "../../PagesUser/SelfServiceCampaignCreation/campaign-creation/invoice-address-modal/CreateInvoiceAddressModal";
import EditInvoiceAddressModal from "../../PagesUser/SelfServiceCampaignCreation/campaign-creation/invoice-address-modal/EditInvoiceAddressModal";
import { useEffect } from "react";
import { useRef } from "react";
import { useState } from "react";
import { withRefluxStore } from "../../Stores";

const EndUserLayout = ({
  children,
  userProfile,
  user,
  atsStats,
  organization,
  // Router
  location,
  history
}) => {
  const { t } = useTranslation();
  const notificationSystemRef = useRef(null);
  const { departmentScout } = useFlags();

  const [modalStates, setModalStates] = useState({
    showCopyCampaignModal: { show: false, metaData: {} },
    showGenericModal: { show: false, metaData: {} }
  });

  const originalBodyOverflow = document.body.style.overflow;

  const { organizationId, superRank, orgas, avatarUrl } = userProfile || {};
  const { unseenApplicantsCount, unseenMessagesCount } = atsStats || {};
  const { displayName } = user || {};

  const isAgency = organization && organization.isAgency;

  const { hasPermissionsToRenderBudget } = getPermissions(userProfile);
  const showAllowPrepaid = !isAgency && hasPermissionsToRenderBudget;

  useEffect(() => {
    const { toggleModalListenRef } = getToggleModalAction();
    const { showCreditModalListenRef } = getShowCreditModalAction();
    const { showInvoiceAddressModalListenRef } = getShowInvoiceAddressModalAction();
    const { showEditInvoiceAddressModalListenRef } = getShowEditInvoiceAddressModalAction();
    const { changeDefaultOrganizationCompletedRef } = getChangeDefaultOrganizationAction();
    const { topUpPrepaidMoneyCompletedRef } = getTopUpPrepaidMoneyAction();
    const { addNotificationListenRef } = getAddNotificationAction();

    const values = queryString.parse(location.search);
    if (values.showVideo == "true") {
      Actions.toggleModal("showGenericModal", true, {
        headline: "Video",
        customWidth: 700,
        description: "called...",
        view: (
          <div style={{ padding: 50 }}>
            <iframe
              title="youtube"
              width="560"
              height="315"
              src="https://www.youtube.com/embed/lZKwmKTB9Vw"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        )
      });

      logger.debug("Dashboard:componentDidMount", "SHOW MODAL...", {});
    }

    return () => {
      toggleModalListenRef();
      showCreditModalListenRef();
      showInvoiceAddressModalListenRef();
      showEditInvoiceAddressModalListenRef();
      changeDefaultOrganizationCompletedRef();
      topUpPrepaidMoneyCompletedRef();
      addNotificationListenRef();
    };
  }, []);

  useEffect(() => {
    //Show beamer notification icon if the organization is an agency
    if (window.Beamer) {
      window.Beamer.init({
        userEmail: window.user.email
      });
    }
  }, [organization]);

  const getToggleModalAction = () => {
    const toggleModalListenRef = Actions.toggleModal.listen((name, showView, metaData) => {
      logger.debug("EndUserLayout:toggleModal", "called...", { name, showView, metaData });

      setModalStates((prev) => ({
        ...prev,
        [name]: { show: showView, metaData }
      }));
    });

    return { toggleModalListenRef };
  };

  const getShowCreditModalAction = () => {
    const showCreditModalListenRef = Actions.showCreditModal.listen((...args) => {
      logger.debug("EndUserLayout:showCreditModal", "called...", args);

      Actions.toggleModal("showCreditModal", true, {
        headline: t("Top up credit"),
        widthVersion: "large",
        view: (
          <div style={{ padding: 15 }}>
            <CreditModal />
          </div>
        )
      });
    });

    return { showCreditModalListenRef };
  };

  const getShowInvoiceAddressModalAction = () => {
    const showInvoiceAddressModalListenRef = Actions.showInvoiceAddressModal.listen((...args) => {
      logger.debug("EndUserLayout:showInvoiceAddressModal", "called...", args);

      Actions.toggleModal("showGenericModal", true, {
        headline: t("Add new billing address"),
        widthVersion: "small",
        view: (
          <div style={{ padding: 15 }}>
            <CreateInvoiceAddressModal
              onCancel={() => Actions.toggleModal("showGenericModal", false)}
              organizationId={organization?.id}
              companyName={organization?.name}
            />
          </div>
        )
      });
    });

    return { showInvoiceAddressModalListenRef };
  };

  const getShowEditInvoiceAddressModalAction = () => {
    const showEditInvoiceAddressModalListenRef = Actions.showEditInvoiceAddressModal.listen(
      (...args) => {
        logger.debug("EndUserLayout:showEditInvoiceAddressModal", "called...", args);

        const contactToEdit = args[0];
        const addressToEdit = args[1];
        const emailToEdit = args[2];

        Actions.toggleModal("showGenericModal", true, {
          headline: t("Edit invoice address"),
          widthVersion: "small",
          view: (
            <div style={{ padding: 15 }}>
              <EditInvoiceAddressModal
                onCancel={() => Actions.toggleModal("showGenericModal", false)}
                contactToEdit={contactToEdit}
                addressToEdit={addressToEdit}
                emailToEdit={emailToEdit}
              />
            </div>
          )
        });
      }
    );

    return { showEditInvoiceAddressModalListenRef };
  };

  const getChangeDefaultOrganizationAction = () => {
    const changeDefaultOrganizationCompletedRef =
      Actions.changeDefaultOrganization.completed.listen(() => {
        logger.log("EndUserLayout:changeDefaultOrganization:completed", "called: ");

        history.push("/run", {
          msg: "Organisation wird gewechselt.",
          redirect: "/",
          showSpinner: true
        });
      });

    return { changeDefaultOrganizationCompletedRef };
  };

  const getTopUpPrepaidMoneyAction = () => {
    // Whenever there is a prepaid package purchased, then update the local prepaid
    // money being shown
    const topUpPrepaidMoneyCompletedRef = Actions.topUpPrepaidMoney.completed.listen((data) => {
      logger.log("EndUserLayout:topUpPrepaidMoney:completed", "called: ", {
        data,
        organizationId: organizationId
      });

      Actions.loadOrganization(organizationId);
    });

    return { topUpPrepaidMoneyCompletedRef };
  };

  const getAddNotificationAction = () => {
    const addNotificationListenRef = Actions.addNotification.listen(function (
      message,
      type,
      keep,
      duration
    ) {
      addNotification(message, type, keep, duration);
    });

    return { addNotificationListenRef };
  };

  const addNotification = (message, type, keep = false, duration = 3) => {
    if (
      notificationSystemRef?.current.state?.notifications?.some(
        (notification) => notification.message === message
      )
    ) {
      return;
    }

    notificationSystemRef?.current.addNotification({
      message: message,
      level: type || "success",
      dismissible: false,
      autoDismiss: keep ? 0 : duration,
      position: "tc"
    });
  };

  const changeOrganization = (selectedOption) => {
    logger.log("EndUserLayout:changeOrganization", `switching to organization: `, selectedOption);
    Actions.changeDefaultOrganization(selectedOption.value);
  };

  const logout = () => {
    Actions.logout();
  };

  const hideNavBar = () => {
    const { pathname, href: currentPage } = window.location;
    const isAssistant = pathname.startsWith("/assistant") && !pathname.includes("dashboard");
    const isJobProfile = /^\/profiles\/[^/]+$/.test(pathname); // matches /profiles/:id only

    return (
      PAGES_WITHOUT_NAVBAR.includes(pathname) ||
      currentPage.includes("/invite") ||
      currentPage.includes("/onboarding") ||
      currentPage.includes("/signup-organization") ||
      currentPage.includes("/budget-check-assistant") ||
      isJobProfile ||
      isAssistant
    );
  };

  return (
    <ThemeProvider theme={theme}>
      {hideNavBar() ? (
        <></>
      ) : (
        <div className="sticky">
          <NavBar
            // user authentication
            superRank={superRank}
            hasATSAccess={organization && organization.atsAccess}
            // userProfile
            hasUserProfile={!!userProfile}
            hasUser={!!user}
            displayName={displayName}
            organizationId={organizationId}
            subAccountHasDashboardAccess={organization && organization.subAccountHasDashboardAccess}
            prepaidBalance={organization && organization.prepaidStateBalance}
            allowPrepaid={showAllowPrepaid}
            orgas={orgas}
            source="dashboard"
            avatarUrl={avatarUrl}
            // atsStats
            unseenApplicantsCount={unseenApplicantsCount}
            unseenMessagesCount={unseenMessagesCount}
            // functions
            logout={logout}
            changeOrganization={changeOrganization}
            signupType={organization && organization.signupType}
            hasDepartmentScoutFlag={departmentScout || false}
          />
        </div>
      )}

      {children}

      {Object.keys(modalStates).map((modalName) => {
        const { show, metaData } = modalStates[modalName];

        return (
          <ModalGeneric
            key={modalName}
            onClose={() => {
              setModalStates((prev) => ({
                ...prev,
                [modalName]: { show: false, metaData: {} }
              }));
              document.body.style.overflow = originalBodyOverflow;
            }}
            hideClose={metaData?.hideClose}
            headline={metaData?.headline}
            text={metaData?.text}
            show={show}
            customWidth={metaData?.customWidth}
            specificUse={metaData?.specificUse}
          >
            {metaData?.view}
          </ModalGeneric>
        );
      })}

      <NotificationSystem ref={notificationSystemRef} style={notificationStyle} allowHTML={true} />
      <ReactTooltip />
    </ThemeProvider>
  );
};

export default withLDConsumer()(
  withRouter(
    withRefluxStore(
      EndUserLayout,
      [Store__System, StatusStore, Store__AtsStats, CampaignMembersStore],
      ["userProfile", "user", "atsStats", "organization"]
    )
  )
);
